import { Button } from "@workspace/web-ui/components/button"
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@workspace/web-ui/components/accordion";
import { ChevronDown } from "lucide-react";
import { useParams } from "@tanstack/react-router";

export const urgentCareFaqs = [
    {
        id: "1",
        title: "How do I resume my treatment chat with Alfie?",
        content:
            `Go to your Home tab and look for "Resume Chat" in your To-Do section. Tap it to continue where you left off.`,
    },
    {
        id: "2",
        title: "I can't complete my payment. What should I do?",
        content:
            "Check your payment method and ensure it's valid. If issues persist, contact our support team for assistance.",
    },
    {
        id: "3",
        title: "What if my symptoms aren't covered by ProxyMedicine?",
        content:
            "Alfie will let you know if your symptoms require in-person medical attention and recommend seeing a healthcare professional.",
    },
    {
        id: "4",
        title: "Is my health information secure?",
        content:
            "Yes, all health data is encrypted and HIPAA compliant. Your privacy and security are our top priorities.",
    },
];

export const weightLossFaqs = [
    {
        id: "1",
        title: "What does the weight loss program include?",
        content:
            "Our program includes personalized medication (if eligible), access to licensed providers, progress tracking, and ongoing support from our care team.",
    },
    {
        id: "2",
        title: "How do I know if I qualify for weight loss medication?",
        content:
            "During your onboarding, a licensed provider will review your medical history and current health to determine if medication is appropriate and safe for you.",
    },
    {
        id: "3",
        title: " How long will it take to see results?",
        content:
            "Results vary per person, but many begin to see progress within a few weeks. Consistency with medication, diet, and lifestyle support are key.",
    },
    {
        id: "4",
        title: "Do I need to follow a specific diet or exercise plan?",
        content:
            "We provide general guidance, but there’s no one-size-fits-all plan. Your provider may offer tailored recommendations to support your weight loss goals.",
    },
    {
        id: "5",
        title: "What if I experience side effects from the medication?",
        content:
            "Mild side effects can occur and usually improve over time. If you’re concerned, you can message your care team directly for advice or adjustments.",
    },
    {
        id: "6",
        title: "How do I request a medication refill?",
        content:
            "You’ll be notified when it’s time to refill. You can also go to your Medication section and request a refill directly from there.",
    },
    {
        id: "7",
        title: "Can I pause or stop the program?",
        content:
            "Yes, you can pause or cancel at any time. Just reach out to your care team so we can guide you through the process safely.",
    },
];

export function SupportPage() {
    const { program } = useParams({ strict: false })

    const faqs = program === 'urgent-care' ? urgentCareFaqs : weightLossFaqs;

    return (
        <section className="py-10 flex w-full flex-col pb-20 gap-6">
            {/* medical emergency section */}
            {program === 'urgent-care' && (
                <div
                    className="flex flex-col gap-[8px] justify-center text-center items-center mb-6 py-[16px] emergency-gradient"
                >
                    <h1 className="text-[#000000] lg:text-[24px] text-[18px] font-medium leading-[130%] tracking-[-2%]">🚨 Medical Emergency 🚨 </h1>
                    <p className="text-[#464B55] lg:text-[16px] text-[12px] leading-[140%] tracking-[-1%] font-normal">If you're experiencing a medical emergency, please call <b>911</b>  or go to your nearest emergency room immediately.</p>
                </div>
            )}
            {/* email support section */}
            <div className="bg-[#EAF2FF] border-[#0404040D] border-[1px] p-[24px] gap-[24px] mx-auto lg:w-[70%] w-full rounded-[12px] flex flex-col text-center">
                <div className="flex justify-center items-center text-center gap-[16px] flex-col">
                    <span className="text-[#1A202C] text-[104px] leading-[104px] font-medium">✉️</span>
                    <h1 className="text-[#1A202C] lg:text-[24px] text-[18px] font-medium leading-[130%] tracking-[-2%]">Email Support</h1>
                    <p className="text-[#464B55] lg:text-[14px] text-[12px] leading-[140%] tracking-[-1%] font-normal">Send us a detailed message about your issue and we'll get back to you within 24 hours. Please include your account email and describe your problem in detail.</p>
                </div>
                <Button style={{ boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.1)" }} className="border-[#DCDCDE] h-auto text-[#fff] lg:text-[16px] text-[12px] w-full lg:w-[90%] !py-[12px] !px-[16px] rounded-[8px]" >
                    appointments@proxymedicine.com
                </Button>
            </div>
            {/* frequently asked questions section */}
            <div className="flex flex-col mt-20 gap-[24px] w-full mx-auto lg:w-[70%]">
                <h1 className="text-[#1A202C] lg:text-[24px] text-[18px] font-medium leading-[130%] text-center tracking-[-2%]">Frequently Asked Questions</h1>
                <div className="flex flex-col gap-[16px]">
                    <Accordion type="single" collapsible className=" w-full rounded-xl overflow-hidden border-[1px] border-solid border-[#2222221A] bg-white !p-0  font-paragraph shadow-[0px_0px_1px_0px_rgba(0,0,0,0.1),0px_2px_2px_0px_rgba(0,0,0,0.09),0px_4px_2px_0px_rgba(0,0,0,0.05),0px_7px_3px_0px_rgba(0,0,0,0.01),0px_10px_3px_0px_rgba(0,0,0,0)] sm:mb-[2%] sm:px-[2%] sm:py-[3%]"
                    >
                        {faqs.map((item) => (
                            <AccordionItem
                                value={item.id}
                                key={item.id}
                                className=" w-full !mb-0  bg-white px-4 py-0 font-paragraph !border-b sm:mb-[2%] sm:px-[2%] sm:py-[3%]"
                            >
                                <AccordionTrigger showChevron={false} className="flex flex-1 items-center justify-between py-2 text-left text-[15px] text-base font-bold leading-6 text-[#4F4D55] transition-all [&>svg>path:last-child]:origin-center [&>svg>path:last-child]:transition-all [&>svg>path:last-child]:duration-200 [&[data-state=open]>svg>path:last-child]:rotate-90 [&[data-state=open]>svg>path:last-child]:opacity-0 [&[data-state=open]>svg]:rotate-180">
                                    <p className="lg:text-xl text-[14px] font-medium capitalize text-[#1A202C]">
                                        {item.title}
                                    </p>

                                    <ChevronDown
                                        size={40}
                                        strokeWidth={1.5}
                                        className="shrink-0 rounded-[555px] bg-[#F2F2F2] p-2 opacity-60 transition-transform duration-200"
                                        aria-hidden="true"
                                    />
                                </AccordionTrigger>
                                <AccordionContent className="pb-2 lg:text-sm text-[12px] font-normal text-[#464B55] sm:text-base md:text-lg">
                                    {item.content}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    )
}
